import { Button } from "@components/components/ui/button";
import type { RangeOption, RangeSelection } from "@hooks/useDataset";

import { DateRangeFilter } from "./DateRangeFilter";
import { SearchBar } from "./SearchBar";

interface FilterPanelProps {
  rangeOptions: readonly RangeOption[];
  activeRange: RangeSelection;
  onRangeChange: (value: RangeSelection) => void;
  customStartDate: string;
  customEndDate: string;
  onCustomStartChange: (value: string) => void;
  onCustomEndChange: (value: string) => void;
  searchValue: string;
  onSearchChange: (value: string) => void;
  filteredCount: number;
  totalCount: number;
}

export function FilterPanel({
  rangeOptions,
  activeRange,
  onRangeChange,
  customStartDate,
  customEndDate,
  onCustomStartChange,
  onCustomEndChange,
  searchValue,
  onSearchChange,
  filteredCount,
  totalCount,
}: FilterPanelProps) {
  const showCustomRange = activeRange === "custom";

  function renderRangeLabel(option: RangeOption): string {
    if (option === 14) {
      return "2周";
    }
    if (option === 30) {
      return "1月";
    }
    return `${option}天`;
  }

  return (
    <div className="mb-6 flex flex-col gap-4 rounded-xl bg-white p-4 shadow-sm sm:p-5">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
        <div className="flex-1">
          <SearchBar value={searchValue} onChange={onSearchChange} />
        </div>
        <div className="flex flex-wrap gap-2">
          {rangeOptions.map((option) => {
            const isActive = option === activeRange;
            return (
              <Button
                key={option}
                variant={isActive ? "default" : "outline"}
                size="sm"
                onClick={() => onRangeChange(option)}
                className={`h-8 rounded-lg text-xs font-medium transition-all sm:text-sm ${
                  isActive
                    ? "bg-accent text-white hover:bg-accent/90 shadow-sm shadow-accent/20 border-transparent"
                    : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50 hover:text-slate-900"
                }`}
              >
                {renderRangeLabel(option)}
              </Button>
            );
          })}
          <Button
            variant={showCustomRange ? "default" : "outline"}
            size="sm"
            onClick={() => onRangeChange("custom")}
            className={`h-8 rounded-lg text-xs font-medium transition-all sm:text-sm ${
              showCustomRange
                ? "bg-accent text-white hover:bg-accent/90 shadow-sm shadow-accent/20 border-transparent"
                : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50 hover:text-slate-900"
            }`}
          >
            自定义
          </Button>
        </div>
      </div>

      {showCustomRange && (
        <div className="animate-fade-in border-t border-slate-100 pt-4">
          <DateRangeFilter
            startDate={customStartDate}
            endDate={customEndDate}
            onStartDateChange={onCustomStartChange}
            onEndDateChange={onCustomEndChange}
          />
        </div>
      )}

      <div className="flex items-center justify-between text-xs text-slate-400">
        <span>
          显示 {filteredCount} / {totalCount} 篇论文
        </span>
      </div>
    </div>
  );
}